import React, { useState } from 'react';
import { Avatar, Dropdown, Menu, Modal } from 'antd';
import { connect } from 'react-redux';
import {
  BellOutlined,
  HeartOutlined,
  LogoutOutlined,
  UserOutlined,
  UserSwitchOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { signOut } from 'redux/actions/Auth';
import { Member, MemberStatus, User } from '@quick/client';
import { Link } from 'react-router-dom';
import { sentenceCase } from 'sentence-case';
import { auth } from '../../auth/firebaseAuth';
import { getNameAvatarImage } from '../../utils/HoaUtils';
import { usePrefetchNotificationPreference } from '../../hooks/Notification';
import { LazyNotifications, LazyProfile } from './index';
import { useCurrentMember, useCurrentUser } from '../../redux/selector';
import { useCommunityPrefixPath } from '../../hooks/Routing';
import { VENDORLINK_PREFIX_PATH } from '../../configs/AppConfig';
import { env } from '../../configs/EnvironmentConfig';

interface MenuItem {
  title: string;
  icon: React.ReactNode;
  path: string;
  onClick?: () => void;
}

const getMenuItems = (appPrefixPath: string, hasActiveMember: boolean, onSignOutClick: () => void): MenuItem[] => {
  const menuItems = [
    {
      title: 'Get help',
      icon: <HeartOutlined />,
      path: `#`,
      onClick: () => Intercom('show'),
    },
    ...(env.REACT_APP_ENV === 'local'
      ? [
          {
            title: 'Switch to vendor',
            icon: <UserSwitchOutlined />,
            path: VENDORLINK_PREFIX_PATH,
          },
        ]
      : []),
    {
      title: 'Sign out',
      icon: <LogoutOutlined />,
      path: `#`,
      onClick: onSignOutClick,
    },
  ];

  const accountSecurityMenuItem = {
    title: 'Account and security',
    icon: <LockOutlined />,
    path: `${appPrefixPath}/account-security`,
  };

  return hasActiveMember
    ? [
        {
          title: 'Profile',
          icon: <UserOutlined />,
          path: `${appPrefixPath}/profile`,
        },
        accountSecurityMenuItem,
        {
          title: 'Notifications',
          icon: <BellOutlined />,
          path: `${appPrefixPath}/notifications`,
        },
        ...menuItems,
      ]
    : [accountSecurityMenuItem, ...menuItems];
};

interface UserInfoProps {
  user: User;
  showText?: boolean;
}

// Used when there is no member
const UserInfo = ({ user, showText = false }: UserInfoProps) => {
  const name = 'New User';
  const profileImg = user.imageUrl ?? getNameAvatarImage(name);

  if (!showText) {
    return <Avatar src={profileImg} />;
  }

  return (
    <div className="d-flex">
      <Avatar size={45} src={profileImg} />
      <div className="pl-3">
        <div className="mb-0 label-l">{name}</div>
      </div>
    </div>
  );
};

interface MemberInfoProps {
  member: Member;
  showText?: boolean;
}

const MemberInfo = ({ member, showText = false }: MemberInfoProps) => {
  const { name, imageUrl, status, role } = member;
  const memberDescription = MemberStatus.Active ? sentenceCase(role) : sentenceCase(status);

  if (!showText) {
    return <Avatar src={imageUrl} />;
  }

  return (
    <div className="d-flex">
      <Avatar size={45} src={imageUrl} />
      <div className="pl-3">
        <div className="mb-0 label-l">{name}</div>
        <span className="text-content-secondary">{memberDescription}</span>
      </div>
    </div>
  );
};

interface NavProfileProps {
  signOut: () => void;
}

export const NavProfile = ({ signOut }: NavProfileProps) => {
  const user = useCurrentUser()!;
  const currentMember = useCurrentMember();
  const hasActiveCurrentMember = currentMember?.status === MemberStatus.Active;

  const [signOutModalVisible, setSignOutModalVisible] = useState(false);
  const onSignOut = async () => {
    await auth.signOut();
    signOut();
  };

  usePrefetchNotificationPreference();
  const onMenuVisible = () => {
    LazyProfile.preload();
    LazyNotifications.preload();
  };
  const appPrefixPath = useCommunityPrefixPath();
  let menuItems = getMenuItems(appPrefixPath, hasActiveCurrentMember, () => setSignOutModalVisible(true));
  const profileMenu = (
    <div className="nav-profile nav-dropdown">
      <div className="nav-profile-header">
        {currentMember ? <MemberInfo showText member={currentMember} /> : <UserInfo showText user={user} />}
      </div>
      <div className="nav-profile-body">
        <Menu selectable={false}>
          {menuItems.map((el, i) => {
            return (
              <Menu.Item key={i} onClick={el.onClick}>
                <div
                  // For Intercom product tour. Construct an ID from non-word characters.
                  id={`profile-nav-${el.title.replaceAll(/(\W)/g, '-').toLowerCase()}`}
                  className="d-flex align-items-center h-100"
                >
                  <span className="mr-3 d-inline-flex align-items-center">{el.icon}</span>
                  <span className="label-s">{el.title}</span>
                  <Link to={el.path ?? '#'} />
                </div>
              </Menu.Item>
            );
          })}
        </Menu>
      </div>
      <Modal
        visible={signOutModalVisible}
        onOk={() => onSignOut()}
        okText="Sign out"
        okType="danger"
        onCancel={() => {
          setSignOutModalVisible(false);
        }}
        cancelButtonProps={{ type: 'text' }}
        closable={false}
        maskClosable
        transitionName=""
        maskTransitionName=""
        width={300}
      >
        <div className="heading-xs">Are you sure?</div>
        <div className="mt-3">You can always log back in at any time.</div>
      </Modal>
    </div>
  );
  return (
    <Dropdown placement="bottomRight" overlay={profileMenu} onVisibleChange={onMenuVisible}>
      <Menu id="profile-nav" className="d-flex align-item-center" mode="horizontal">
        <Menu.Item className="p-0">
          {currentMember ? <MemberInfo member={currentMember} /> : <UserInfo user={user} />}
        </Menu.Item>
      </Menu>
    </Dropdown>
  );
};

export default connect(null, { signOut })(NavProfile);
